import React, { useEffect, useMemo, useState } from "react";
import "./license-key-manager.css";
import { licenseAPI } from "../../../utils/api";

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "available", label: "Available" },
  { value: "redeemed", label: "Redeemed" },
  { value: "revoked", label: "Revoked" },
];

const LicenseKeyManager = () => {
  const [licenses, setLicenses] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ totalPages: 1, totalCount: 0 });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [generateCount, setGenerateCount] = useState(10);
  const [batchTag, setBatchTag] = useState("");
  const [notes, setNotes] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 350);

    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    const fetchLicenses = async () => {
      try {
        setLoading(true);
        const response = await licenseAPI.listPool({
          status: statusFilter,
          search: debouncedSearch,
          page,
          limit: pageSize,
        });

        const data = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        const pagination = response?.data?.pagination || {
          totalPages: 1,
          totalCount: data.length,
        };

        setLicenses(data);
        setMeta(pagination);

        if (
          pagination.totalPages &&
          pagination.totalPages > 0 &&
          page > pagination.totalPages
        ) {
          setPage(pagination.totalPages);
        }
      } catch (error) {
        console.error("Failed to fetch licenses", error);
        setLicenses([]);
        setMeta({ totalPages: 1, totalCount: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchLicenses();
  }, [statusFilter, debouncedSearch, page, refreshKey]);

  const summary = useMemo(() => {
    const counts = licenses.reduce(
      (acc, license) => {
        acc[license.status] = (acc[license.status] || 0) + 1;
        return acc;
      },
      { available: 0, redeemed: 0, revoked: 0 }
    );

    return counts;
  }, [licenses]);

  const handleGenerateLicenses = async (event) => {
    event.preventDefault();
    const quantity = Number.isNaN(generateCount) ? 1 : generateCount;
    if (quantity < 1) {
      alert("Please select at least one license");
      return;
    }

    try {
      setActionLoading(true);
      const response = await licenseAPI.generatePool({
        quantity,
        batchTag,
        notes,
      });
      const generated = response?.data?.licenses || [];

      if (generated.length > 0) {
        alert(
          `Generated ${generated.length} license${
            generated.length === 1 ? "" : "s"
          }.`
        );
      }
      // Reset form fields
      setBatchTag("");
      setNotes("");
      // Reset filters to show newly generated licenses
      setStatusFilter("all");
      setSearchInput("");
      setPage(1);
      // Trigger data refresh
      setRefreshKey((key) => key + 1);
    } catch (error) {
      console.error("Failed to generate licenses", error);
      const errorMessage = 
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Unable to generate licenses. Please try again.";
      
      // Log full error details for debugging
      if (error?.response) {
        console.error("Error response:", error.response.data);
        console.error("Error status:", error.response.status);
      }
      
      // If authentication error, suggest logging in again
      if (error?.response?.status === 401) {
        const shouldReload = window.confirm(
          "Your session has expired. Please log in again.\n\nClick OK to reload the page."
        );
        if (shouldReload) {
          window.location.reload();
        }
      } else {
        alert(errorMessage);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRedeem = async (licenseKey) => {
    const studentEmail = window.prompt(
      "Enter the student email that should receive this license:"
    );
    if (!studentEmail) return;

    const studentName = window.prompt(
      "Optional: enter the student name for quick reference",
      ""
    );

    // Optional: Ask for hardware ID (device UUID)
    const hardwareID = window.prompt(
      "Optional: Enter the device hardware ID (UUID) to bind this license to a specific device.\n\nLeave empty if you want to bind it later during activation.",
      ""
    );

    try {
      setActionLoading(true);
      const redeemPayload = {
        licenseKey,
        studentEmail,
        redeemedForName: studentName,
      };

      // Add hardwareID only if provided
      if (hardwareID && hardwareID.trim()) {
        redeemPayload.hardwareID = hardwareID.trim();
      }

      await licenseAPI.redeemPool(redeemPayload);
      alert(
        hardwareID && hardwareID.trim()
          ? "License marked as redeemed, assigned, and bound to device."
          : "License marked as redeemed and assigned."
      );
      setRefreshKey((key) => key + 1);
    } catch (error) {
      console.error("Failed to redeem license", error);
      alert(
        error?.response?.data?.message ||
          "Unable to redeem license. Please verify the email belongs to an existing student."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopy = (licenseKey) => {
    navigator.clipboard.writeText(licenseKey);
    alert("License key copied to clipboard");
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setSearchInput("");
    setPage(1);
    setRefreshKey((key) => key + 1);
  };

  return (
    <div className="license-key-manager">
      <section className="panel generate-panel">
        <div className="panel-header">
          <div>
            <h2>Generate License Keys</h2>
            <p>Create single-use licenses for distribution</p>
          </div>
          <button
            className="refresh-button"
            onClick={() => setRefreshKey((key) => key + 1)}
            disabled={loading}
          >
            ⟳ Refresh
          </button>
        </div>

        <form className="generate-form" onSubmit={handleGenerateLicenses}>
          <label>
            Quantity
            <input
              type="number"
              min="1"
              max="100"
              value={generateCount}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                setGenerateCount(Number.isNaN(value) ? 1 : value);
              }}
            />
          </label>
          <label>
            Batch Tag
            <input
              type="text"
              placeholder="Optional label (e.g. Jan Campaign)"
              value={batchTag}
              onChange={(e) => setBatchTag(e.target.value)}
              maxLength={100}
            />
          </label>
          <label className="notes-field">
            Notes
            <textarea
              rows="2"
              placeholder="Optional notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </label>
          <button
            type="submit"
            className="primary-btn"
            disabled={actionLoading}
          >
            {actionLoading ? "Generating..." : "Generate"}
          </button>
        </form>
      </section>

      <section className="panel inventory-panel">
        <div className="panel-header">
          <div>
            <h2>License Inventory</h2>
            <p>
              {loading
                ? "Loading licenses..."
                : `${meta.totalCount} total licenses in this view`}
            </p>
          </div>
          <button className="link-button" onClick={resetFilters}>
            Reset filters
          </button>
        </div>

        <div className="filters">
          <div className="status-filters">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                className={`filter-chip ${
                  statusFilter === filter.value ? "active" : ""
                }`}
                onClick={() => {
                  setStatusFilter(filter.value);
                  setPage(1);
                }}
              >
                {filter.label}
                {filter.value !== "all" && summary[filter.value] > 0
                  ? ` (${summary[filter.value]})`
                  : ""}
              </button>
            ))}
          </div>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by key, email, batch"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                type="button"
                className="clear-btn"
                onClick={() => setSearchInput("")}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="table-wrapper">
          <table className="licenses-table">
            <thead>
              <tr>
                <th>License Key</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Device ID</th>
                <th>Batch</th>
                <th>Created</th>
                <th>Redeemed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {licenses.length === 0 ? (
                <tr>
                  <td colSpan={8} className="no-data">
                    {loading
                      ? "Fetching licenses..."
                      : "No licenses found for the selected filters."}
                  </td>
                </tr>
              ) : (
                licenses.map((license) => (
                  <tr key={license._id}>
                    <td>
                      <div className="key-cell">
                        <span className="license-value">
                          {license.licenseKey}
                        </span>
                        <button
                          className="icon-button"
                          onClick={() => handleCopy(license.licenseKey)}
                          title="Copy license key"
                        >
                          📋
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className={`status-pill ${license.status}`}>
                        {license.status}
                      </span>
                    </td>
                    <td className="assigned-cell">
                      {license.redeemedForEmail ? (
                        <>
                          <strong>{license.redeemedForName || "—"}</strong>
                          <span>{license.redeemedForEmail}</span>
                        </>
                      ) : (
                        <em>Not assigned</em>
                      )}
                    </td>
                    <td className="device-id-cell">
                      {license.hardwareID ? (
                        <div className="device-id-display">
                          <code
                            className="device-id-value"
                            title={license.hardwareID}
                          >
                            {license.hardwareID.substring(0, 16)}...
                          </code>
                          <button
                            className="icon-button-small"
                            onClick={() => {
                              navigator.clipboard.writeText(license.hardwareID);
                              alert("Device ID copied to clipboard");
                            }}
                            title="Copy full device ID"
                          >
                            📋
                          </button>
                        </div>
                      ) : (
                        <em style={{ color: "#999" }}>Not bound</em>
                      )}
                    </td>
                    <td>{license.batchTag || "—"}</td>
                    <td>
                      {license.createdAt
                        ? new Date(license.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      {license.redeemedAt
                        ? new Date(license.redeemedAt).toLocaleString()
                        : "—"}
                    </td>
                    <td>
                      <div className="row-actions">
                        {license.status === "available" && (
                          <button
                            className="secondary-btn"
                            onClick={() => handleRedeem(license.licenseKey)}
                            disabled={actionLoading}
                          >
                            Redeem
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {meta.totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page === 1}
            >
              ← Prev
            </button>
            <span>
              Page {page} of {meta.totalPages}
            </span>
            <button
              onClick={() =>
                setPage((current) =>
                  Math.min(meta.totalPages, current + 1 || 1)
                )
              }
              disabled={page >= meta.totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default LicenseKeyManager;
